import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authService, getErrorMessage } from '@/services'
import { useAuthStore } from '@/store/auth'
import { UpdateProfileRequest, GitHubSelectOrgRequest } from '@/types'

export function useMe() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const setUser = useAuthStore((state) => state.setUser)

  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const user = await authService.getMe()
      setUser(user)
      return user
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore((state) => state.setUser)

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => authService.updateProfile(data),
    onSuccess: (user) => {
      setUser(user)
      queryClient.invalidateQueries({ queryKey: ['me'] })
      toast.success('Profile updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useLogout() {
  const logout = useAuthStore((state) => state.logout)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return () => {
    logout()
    queryClient.clear()
    navigate('/login')
    toast.success('Logged out')
  }
}

export function useGitHubAuth() {
  return useMutation({
    mutationFn: () => authService.getGitHubAuthUrl(),
    onSuccess: (response) => {
      // Store state for CSRF validation on callback
      sessionStorage.setItem('github_oauth_state', response.state)
      window.location.href = response.auth_url
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useGitHubCallback() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const setPendingOrgSelection = useAuthStore((state) => state.setPendingOrgSelection)
  const navigate = useNavigate()

  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      authService.handleGitHubCallback(code, state),
    onSuccess: (response) => {
      if (response.requires_org_selection && response.organizations && response.pending_token) {
        // Multiple orgs - need selection
        setPendingOrgSelection(response.pending_token, response.organizations)
        navigate('/auth/github/select-org')
      } else if (response.user && response.access_token && response.refresh_token && response.expires_at) {
        // Single org or pre-selected - complete login
        setAuth(response.user, response.access_token, response.refresh_token, response.expires_at)
        toast.success('Welcome!')
        navigate('/dashboard')
      } else {
        toast.error('Unexpected response from server')
        navigate('/login')
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
      navigate('/login')
    },
  })
}

export function useGitHubSelectOrg() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const clearPendingOrgSelection = useAuthStore((state) => state.clearPendingOrgSelection)
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: GitHubSelectOrgRequest) => authService.selectGitHubOrg(data),
    onSuccess: (response) => {
      clearPendingOrgSelection()
      setAuth(response.user, response.access_token, response.refresh_token, response.expires_at)
      toast.success('Welcome!')
      navigate('/dashboard')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
